import { useState } from 'react';
import { Button, Drawer, Flex, Typography } from 'antd';
import styled from '@emotion/styled';
import { CaretDownIcon } from '@phosphor-icons/react';
import {
  FacilitySelect,
  type FacilitySelectValue,
} from '@admin-panel/components/lib/select/facility/facilityselect';
import { VendorSelect } from '@admin-panel/components/lib/select/vendor/vendorselect';
import { FacilityType, IERPUser, IFacility, IVendor } from '@admin-panel/types';
import { useOptionalVendorGlobalContext } from '@admin-panel/hooks';

type VendorContextShape = {
  vendor?: IVendor;
  setVendor: (vendor: IVendor | undefined) => void;
  vendorFacility?: IFacility;
  setVendorFacility: (vendorFacility: IFacility | undefined) => void;
};

const isSuperAdmin = (user?: IERPUser) =>
  Boolean(user?.roles?.some(role => role.name === 'SUPER_ADMIN'));

const isNexusSuperAdmin = (user?: IERPUser) =>
  Boolean(user?.roles?.some(role => role.name === 'NEXUS_SUPER_ADMIN'));

const VendorSelectorInner = ({
  user,
  vendorContext,
}: {
  user?: IERPUser;
  vendorContext: VendorContextShape;
}) => {
  const { vendor, setVendor, vendorFacility, setVendorFacility } = vendorContext;
  const [pendingVendor, setPendingVendor] = useState<IVendor | undefined>(vendor);
  const [pendingFacility, setPendingFacility] = useState<IFacility | undefined>(vendorFacility);
  const displayVendor = pendingVendor ?? vendor;
  const displayFacility =
    displayVendor?.id === vendor?.id ? (pendingFacility ?? vendorFacility) : pendingFacility;

  const handleVendorFacilitySelection = (selectedValue: FacilitySelectValue) => {
    const nextValue = Array.isArray(selectedValue)
      ? selectedValue[selectedValue.length - 1]
      : selectedValue;

    if (nextValue === undefined) {
      setVendorFacility(undefined);
      return;
    }
  };

  const handleVendorSelectionChange = (selectedValue: number | number[] | undefined | null) => {
    if (Array.isArray(selectedValue)) {
      selectedValue = selectedValue[0];
    }

    if (!selectedValue) {
      setPendingVendor(undefined);
      setPendingFacility(undefined);
      return;
    }

    setPendingFacility(undefined);
  };

  if (isNexusSuperAdmin(user)) {
    const handlePendingVendor = (nextVendor: IVendor | undefined) => {
      setPendingVendor(nextVendor);
      setPendingFacility(undefined);
    };

    const handlePendingFacilitySelection = (selectedValue: FacilitySelectValue) => {
      const nextValue = Array.isArray(selectedValue)
        ? selectedValue[selectedValue.length - 1]
        : selectedValue;

      if (nextValue === undefined) {
        setPendingFacility(undefined);
      }
    };

    const handlePendingFacility = (facility?: IFacility) => {
      setPendingFacility(facility);

      const activeVendor = pendingVendor ?? vendor;
      if (!activeVendor || !facility) {
        return;
      }

      setVendor(activeVendor);
      setVendorFacility(facility);
    };

    return (
      <Flex vertical={false} justify="flex-start" gap={8} align="center">
        <VendorSelect
          status={displayVendor === undefined ? 'error' : ''}
          value={displayVendor?.id}
          style={{
            width: 320,
          }}
          showSearch={false}
          setVendor={handlePendingVendor}
          onSelect={handleVendorSelectionChange}
          selectedVendor={displayVendor}
          onChange={handleVendorSelectionChange}
          allowClear
        />
        {displayVendor && (
          <FacilitySelect
            key={`facility-${displayVendor.id}`}
            status={displayFacility?.id === undefined ? 'error' : ''}
            value={displayFacility?.id}
            style={{
              width: 250,
            }}
            showSearch={false}
            filters={[
              {
                field: 'vendorId',
                operator: 'eq',
                value: displayVendor.id,
              },
              {
                field: 'facilityType',
                operator: 'eq',
                value: FacilityType.VENDOR,
              },
            ]}
            onChange={handlePendingFacilitySelection}
            onSelect={handlePendingFacilitySelection}
            setFacility={handlePendingFacility}
          />
        )}
      </Flex>
    );
  }

  if (user?.userType === 'internal' && isSuperAdmin(user)) {
    return (
      <VendorSelect
        status={vendor === undefined ? 'error' : ''}
        value={vendor?.id}
        style={{
          width: 400,
        }}
        showSearch={false}
        setVendor={setVendor}
        selectedVendor={vendor}
      />
    );
  }

  if (user?.userType === 'vendor' && vendor) {
    return (
      <Flex vertical={false} justify="flex-start" gap={4} align="center">
        <Typography.Text
          style={{
            fontWeight: 'bold',
            fontSize: 16,
            textAlign: 'center',
          }}
        >
          {vendor.name}
        </Typography.Text>
        <FacilitySelect
          status={vendorFacility?.id == undefined ? 'error' : ''}
          value={vendorFacility?.id}
          style={{
            width: 250,
          }}
          showSearch={false}
          filters={[
            {
              field: 'vendorId',
              operator: 'eq',
              value: vendor.id,
            },
            {
              field: 'facilityType',
              operator: 'eq',
              value: FacilityType.VENDOR,
            },
          ]}
          onChange={handleVendorFacilitySelection}
          onSelect={handleVendorFacilitySelection}
          setFacility={setVendorFacility}
        />
      </Flex>
    );
  }

  return null;
};

const MobileContextPicker = styled.div`
  .ant-flex {
    flex-direction: column;
    align-items: stretch;
    gap: 12px !important;
  }
  .ant-select {
    width: 100% !important;
    min-width: 0;
    height: 44px;
  }
  .ant-select-selector {
    min-height: 44px;
    font-size: 16px;
  }
`;

export const VendorSelector = ({
  user,
  compact = false,
}: {
  user?: IERPUser;
  compact?: boolean;
}) => {
  const vendorContext = useOptionalVendorGlobalContext();
  const [contextOpen, setContextOpen] = useState(false);

  if (!vendorContext) {
    return null;
  }

  const contextKey = `${vendorContext.vendor?.id ?? 'none'}-${
    vendorContext.vendorFacility?.id ?? 'none'
  }`;

  const selector = (
    <VendorSelectorInner key={contextKey} user={user} vendorContext={vendorContext} />
  );
  if (!compact) return selector;
  const vendorName = vendorContext.vendor?.name || 'Choose vendor';
  const facilityName = vendorContext.vendorFacility?.name || 'Choose facility';
  return (
    <>
      <Button
        className="vendor-working-context"
        aria-label={`Change vendor and facility: ${vendorName}, ${facilityName}`}
        title={`${vendorName} · ${facilityName}`}
        onClick={() => setContextOpen(true)}
      >
        <Flex gap={6} align="center" style={{ width: '100%', minWidth: 0 }}>
          <div style={{ minWidth: 0, flex: 1, textAlign: 'left' }}>
            <Typography.Text
              strong
              ellipsis
              style={{ display: 'block', fontSize: 13, lineHeight: '18px' }}
            >
              {vendorName}
            </Typography.Text>
            <Typography.Text
              type="secondary"
              ellipsis
              style={{ display: 'block', fontSize: 12, lineHeight: '16px' }}
            >
              {facilityName}
            </Typography.Text>
          </div>
          <CaretDownIcon aria-hidden />
        </Flex>
      </Button>
      <Drawer
        open={contextOpen}
        destroyOnHidden
        onClose={() => setContextOpen(false)}
        title="Working vendor & facility"
        placement="bottom"
        height="min(380px, 90dvh)"
        extra={
          <Button style={{ minHeight: 44 }} onClick={() => setContextOpen(false)}>
            Done
          </Button>
        }
      >
        <MobileContextPicker>{selector}</MobileContextPicker>
      </Drawer>
    </>
  );
};
